import React, { Suspense, useState } from "react";

import { useAppSelector } from "../../hooks/hooks";
import Left from "../../assets/images/Left.svg";
import Right from "../../assets/images/Right.svg";

const Hero: React.FC = () => {
  const { data: appData } = useAppSelector((state) => state.appData);

  // prefer sliders array and map to banner-like items (images)
  const rawSliders = appData?.sliders ?? [];
  // take up to 3 sliders and convert to simple image items
  const bannersFromSliders = (Array.isArray(rawSliders) ? rawSliders : [])
    .slice(0, 3)
    .map((s: any) => ({
      id: s.id,
      title: s.title,
      url: s.url,
      image: s.image_url ?? null,
    }));

  const items = bannersFromSliders.length
    ? bannersFromSliders
    : (appData?.banners ?? []);

  const [activeSlide, setActiveSlide] = useState<number>(0);

  const nextSlide = (): void => {
    setActiveSlide((prev) => (prev + 1) % items.length);
  };

  const prevSlide = (): void => {
    setActiveSlide((prev) => (prev - 1 + items.length) % items.length);
  };

  const goToSlide = (index: number): void => {
    setActiveSlide(index);
  };

  if (!items || items.length === 0) {
    return (
      <div className="hero-placeholder">
        <div className="h-56 bg-gray-100 flex items-center justify-center">
          <span className="text-gray-400">No banners available</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full overflow-hidden" data-carousel="static">
      {/* Slider Container */}
      <div className="relative h-48 sm:h-60 md:h-72 lg:h-[334px] w-full">
        {items.map((slide: any, index: number) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              index === activeSlide ? "opacity-100 z-10" : "opacity-0"
            }`}
          >
            <a
              href={slide.url || "#"}
              target={
                slide.url && slide.url.startsWith("http") ? "_blank" : undefined
              }
              rel="noopener noreferrer"
            >
              <img
                src={slide.image}
                alt={`Slide ${index + 1}`}
                className="w-full h-full object-center object-cover"
              />
            </a>
          </div>
        ))}
      </div>

      {/* Navigation Buttons */}
      <button
        type="button"
        className="absolute top-1/2 left-4 z-30 flex items-center justify-center transform -translate-y-1/2 cursor-pointer group focus:outline-none"
        onClick={prevSlide}
      >
        <img src={Left} alt="Previous" className="w-4 h-4 lg:w-10 lg:h-10" />
      </button>
      <button
        type="button"
        className="absolute top-1/2 right-4 z-30 flex items-center justify-center transform -translate-y-1/2 cursor-pointer group focus:outline-none"
        onClick={nextSlide}
      >
        <img src={Right} alt="Next" className="w-4 h-4 lg:w-10 lg:h-10" />
      </button>

      {/* Indicators */}
      <div className="flex justify-center items-center py-4">
        {items.map((_: any, index: number) => (
          <button
            key={index}
            type="button"
            className={`w-3 h-1 rounded-sm mx-2 transition-all duration-300 ${
              index === activeSlide
                ? "bg-blue-500 scale-125"
                : "bg-gray-300 hover:scale-125"
            }`}
            aria-current={index === activeSlide}
            aria-label={`Slide ${index + 1}`}
            onClick={() => goToSlide(index)}
          ></button>
        ))}
      </div>
    </div>
  );
};

export default Hero;
